import React, { useState, useCallback, useEffect } from "react";
import moment from "moment";
import {
  Button,
  Select,
  Table,
  DatePicker,
  Radio,
  Row,
  Col,
  notification,
} from "antd";
import { DrawerContainer } from "../styles";
import ButtonStandard from "../../../../components/utils/button";

const { Option } = Select;
const dateFormat = "DD/MM/YYYY";

const RelateDrawer = ({
  visible,
  onClose,
  herds,
  selectedAnimals,
  relatedCountByHerdId,
  onSave,
  onSaveEdit,
  editGroup,
  translation,
}) => {
  const t = translation?.boitelContractsPage?.selectAnimals?.relateDrawer || {};
  const tPage = translation?.boitelContractsPage?.selectAnimals || {};
  const animalCol = translation?.animal?.columns || {};

  const [selectedHerdId, setSelectedHerdId] = useState(null);
  const [entryDateType, setEntryDateType] = useState("single");
  const [singleEntryDate, setSingleEntryDate] = useState(null);
  const [perAnimalEntryDates, setPerAnimalEntryDates] = useState({});

  const isEditMode = Boolean(editGroup);

  const selectedHerd = herds?.find((h) => h.id === selectedHerdId) || null;
  const amountConfirmed =
    selectedHerd?.amountAnimalsConfirmed ??
    selectedHerd?.amount_animals_confirmed ??
    0;
  const alreadyInHerd = selectedHerdId
    ? relatedCountByHerdId?.[selectedHerdId] || 0
    : 0;
  const availableSlots = selectedHerd
    ? (selectedHerd.amountAnimals || 0) - alreadyInHerd - amountConfirmed
    : 0;
  const selectedCount = selectedAnimals?.length || 0;

  useEffect(() => {
    if (visible && editGroup) {
      setSelectedHerdId(editGroup.herdId);
      const animals = editGroup.animals || [];
      const dates = animals.map((a) => a.animalEntryDate).filter(Boolean);
      const allSame = dates.length > 0 && dates.every((d) => d === dates[0]);
      if (allSame && dates[0]) {
        setEntryDateType("single");
        setSingleEntryDate(dates[0]);
        setPerAnimalEntryDates({});
      } else {
        setEntryDateType("specific");
        setSingleEntryDate(null);
        const map = {};
        animals.forEach((a) => {
          if (a.animalEntryDate) map[a.animalId] = a.animalEntryDate;
        });
        setPerAnimalEntryDates(map);
      }
    }
  }, [visible, editGroup]);

  const resetForm = useCallback(() => {
    setSelectedHerdId(null);
    setEntryDateType("single");
    setSingleEntryDate(null);
    setPerAnimalEntryDates({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const validateAndSave = useCallback(() => {
    if (!selectedHerdId) {
      notification.warning({
        message: t.selectHerdRequired || "Selecione um conjunto.",
      });
      return;
    }

    if (isEditMode && onSaveEdit) {
      if (entryDateType === "single") {
        const dateVal =
          singleEntryDate &&
          (typeof singleEntryDate === "string"
            ? singleEntryDate
            : moment(singleEntryDate).format("YYYY-MM-DD"));
        if (!dateVal) {
          notification.warning({
            message: t.entryDateRequired || "Informe a data de entrada.",
          });
          return;
        }
        onSaveEdit(selectedHerdId, dateVal, null);
      } else {
        const missing = (selectedAnimals || []).filter(
          (a) => !perAnimalEntryDates[a.id],
        );
        if (missing.length > 0) {
          notification.warning({
            message:
              t.entryDateAllRequired ||
              "Informe a data de entrada para todos os animais.",
          });
          return;
        }
        onSaveEdit(selectedHerdId, null, perAnimalEntryDates);
      }
      resetForm();
      onClose();
      return;
    }

    const herd = herds.find((h) => h.id === selectedHerdId);
    if (!herd) return;

    if (selectedCount > herd.amountAnimals) {
      notification.error({
        message:
          t.validationExceedsAmount ||
          "A quantidade de animais selecionados ultrapassa a quantidade do conjunto.",
      });
      return;
    }

    if (selectedCount > availableSlots) {
      notification.error({
        message:
          t.validationNoSlots ||
          "Não há vagas suficientes no conjunto para os animais selecionados.",
      });
      return;
    }

    const breedId = herd.breedId || herd.breed_id;
    const animalsWithWrongBreed = (selectedAnimals || []).filter(
      (a) => (a.breedId || a.breed_id) !== breedId,
    );
    if (animalsWithWrongBreed.length > 0) {
      notification.warning({
        message:
          t.validationBreedMismatch ||
          "Existem animais com raças diferentes do previsto no conjunto selecionado.",
      });
    }

    if (entryDateType === "single") {
      if (!singleEntryDate) {
        notification.warning({
          message: t.entryDateRequired || "Informe a data de entrada.",
        });
        return;
      }
      onSave(selectedHerdId, singleEntryDate, null);
    } else {
      const missing = (selectedAnimals || []).filter(
        (a) => !perAnimalEntryDates[a.id],
      );
      if (missing.length > 0) {
        notification.warning({
          message:
            t.entryDateAllRequired ||
            "Informe a data de entrada para todos os animais.",
        });
        return;
      }
      onSave(selectedHerdId, null, perAnimalEntryDates);
    }

    resetForm();
    onClose();
  }, [
    selectedHerdId,
    herds,
    selectedCount,
    availableSlots,
    selectedAnimals,
    entryDateType,
    singleEntryDate,
    perAnimalEntryDates,
    onSave,
    onSaveEdit,
    isEditMode,
    onClose,
    resetForm,
    t,
  ]);

  const handleSave = () => {
    validateAndSave();
  };

  const herdLabel = (herd) => {
    const breed = herd.breedName || herd.breed_name || "";
    const gender = herd.gender
      ? translation?.boitelContractsPage?.table?.column?.animalGenderOptions?.[
          herd.gender
        ] || herd.gender
      : "";
    return `${gender} / ${breed}`.trim() || herd.id;
  };

  const columns = [
    {
      title: animalCol.handlingNumber || "Identificação",
      dataIndex: "handlingNumber",
      key: "handlingNumber",
      render: (_, record) =>
        record.handlingNumber ||
        record.tagId ||
        record.sisbov ||
        record.nickname ||
        "-",
    },
    {
      title: animalCol.tagId || "Id. Eletrônica",
      dataIndex: "tagId",
      key: "tagId",
      render: (t) => t || "-",
    },
    {
      title: animalCol.sisbov || "Sisbov",
      dataIndex: "sisbov",
      key: "sisbov",
      render: (t) => t || "-",
    },
    {
      title: animalCol.gender || "Sexo",
      dataIndex: "gender",
      key: "gender",
      render: (g) =>
        g === "M"
          ? translation?.animal?.details?.male || "Macho"
          : translation?.animal?.details?.female || "Fêmea",
    },
    {
      title: animalCol.breed || "Raça",
      dataIndex: "breedName",
      key: "breedName",
      render: (t, r) => t || r.breed_name || "-",
    },
    ...(entryDateType === "specific"
      ? [
          {
            title: t.entryDate || "Data de Entrada",
            key: "animalEntryDate",
            render: (_, record) => (
              <DatePicker
                style={{ width: "100%" }}
                format={dateFormat}
                allowClear
                disabledDate={(currentDate) => currentDate.isAfter(moment())}
                value={
                  perAnimalEntryDates[record.id]
                    ? moment(perAnimalEntryDates[record.id])
                    : null
                }
                onChange={(date) =>
                  setPerAnimalEntryDates((prev) => ({
                    ...prev,
                    [record.id]: date ? date.format("YYYY-MM-DD") : null,
                  }))
                }
              />
            ),
          },
        ]
      : [
          {
            title: t.entryDate || "Data de Entrada",
            dataIndex: "_entry",
            key: "_entry",
            render: () =>
              singleEntryDate
                ? moment(singleEntryDate).format(dateFormat)
                : "-",
          },
        ]),
  ];

  return (
    <DrawerContainer
      title={
        isEditMode
          ? tPage.relateDrawerEditTitle || "Editar relacionamento do conjunto"
          : t.title || "Relacionar animais ao conjunto"
      }
      width={720}
      visible={visible}
      onClose={handleClose}
    >
      <Row type="flex" gutter={[16, 16]}>
        <Col span={24}>
          <label>{t.selectHerd || "Conjunto do contrato"}</label>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder={t.selectHerdPlaceholder || "Selecione o conjunto"}
            value={selectedHerdId || undefined}
            onChange={setSelectedHerdId}
            disabled={isEditMode}
          >
            {(herds || []).map((h) => {
              const confirmed =
                h.amountAnimalsConfirmed ?? h.amount_animals_confirmed ?? 0;
              const entryDate = moment(h.entryDate).format(dateFormat);
              const vagas =
                (h.amountAnimals || 0) -
                (relatedCountByHerdId?.[h.id] || 0) -
                confirmed;
              return (
                <Option key={h.id} value={h.id}>
                  {herdLabel(h)} / Data de entrada: {entryDate} (Qtd:{" "}
                  {h.amountAnimals || 0}, Vagas: {vagas})
                </Option>
              );
            })}
          </Select>
        </Col>
        <Col span={24}>
          <label>{t.entryDateType || "Data de entrada"}</label>
          <Radio.Group
            value={entryDateType}
            onChange={(e) => setEntryDateType(e.target.value)}
            style={{ marginTop: 8, display: "block" }}
          >
            <Radio value="single">
              {t.entryDateSingle || "Data única para todos"}
            </Radio>
            <Radio value="specific">
              {t.entryDatePerAnimal || "Data específica por animal"}
            </Radio>
          </Radio.Group>
          {entryDateType === "single" && (
            <DatePicker
              style={{ width: "100%", marginTop: 8 }}
              format={dateFormat}
              allowClear
              disabledDate={(currentDate) => currentDate.isAfter(moment())}
              value={singleEntryDate ? moment(singleEntryDate) : null}
              onChange={(date) =>
                setSingleEntryDate(date ? date.format("YYYY-MM-DD") : null)
              }
            />
          )}
        </Col>
        <Col span={24}>
          <label>{t.selectedAnimalsList || "Animais selecionados"}</label>
          <Table
            rowKey="id"
            dataSource={selectedAnimals || []}
            columns={columns}
            pagination={{
              pageSize: 10,
              hideOnSinglePage:
                selectedAnimals != null && selectedAnimals.length > 10
                  ? false
                  : true,
            }}
            size="small"
            style={{ marginTop: 8 }}
            scroll={{ x: 600 }}
          />
        </Col>
      </Row>
      <div
        style={{
          borderTop: "1px solid #e8e8e8",
          padding: "10px 0 0",
          marginTop: 24,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <Button onClick={handleClose}>
          {translation?.buttons?.cancel || "Cancelar"}
        </Button>
        <ButtonStandard buttonType="type6" onClick={handleSave}>
          {translation?.buttons?.save || "Salvar"}
        </ButtonStandard>
      </div>
    </DrawerContainer>
  );
};

export default RelateDrawer;
